import {
  BLUE,
  EMPTY,
  MAX_LEVEL,
  RED,
  legalMovesFor,
  opponent,
  simulateMove,
  terminalWinner,
  totalsFor,
} from "./engine.js";

const SEARCH_LIMITS = [Infinity, 18, 14, 10, 8];
const WIN_SCORE = 1_000_000;

function isEarlyPhase(state, cells) {
  const totalBalls = state.levels.reduce((sum, level) => sum + level, 0);
  const radius = cells.reduce((largest, cell) => Math.max(largest, cell.distance), 0);
  return state.turnsPlayed < Math.max(12, radius * 4)
    || totalBalls < Math.max(radius * 8, cells.length / 2);
}

function neighborhoodProfile(state, cells, index, player) {
  let friendlyAdjacent = 0;
  let enemyAdjacent = 0;
  let friendlyMaxLevel = 0;
  let enemyMaxLevel = 0;

  for (const neighbor of cells[index].neighbors) {
    const owner = state.owners[neighbor];
    const level = state.levels[neighbor];
    if (owner === player) {
      friendlyAdjacent += 1;
      friendlyMaxLevel = Math.max(friendlyMaxLevel, level);
    } else if (owner === opponent(player)) {
      enemyAdjacent += 1;
      enemyMaxLevel = Math.max(enemyMaxLevel, level);
    }
  }

  return {
    friendlyAdjacent,
    enemyAdjacent,
    friendlyMaxLevel,
    enemyMaxLevel,
  };
}

function playerTotals(totals, player) {
  return player === RED
    ? { score: totals.redScore, tiles: totals.redTiles }
    : { score: totals.blueScore, tiles: totals.blueTiles };
}

function blastPotential(state, cells, index, player) {
  let value = cells[index].neighbors.length * 3;
  for (const neighbor of cells[index].neighbors) {
    const owner = state.owners[neighbor];
    const level = state.levels[neighbor];
    if (owner === opponent(player)) {
      value += 14 + level * 8;
      if (level === MAX_LEVEL - 1) value += 34;
    } else if (owner === player) {
      value += level >= MAX_LEVEL - 2 ? 8 : 2;
    } else {
      value += 1;
    }
  }
  return value;
}

function movePriority(state, cells, move, player) {
  const owner = state.owners[move];
  const level = state.levels[move];
  const resultingLevel = level + 1;
  const cell = cells[move];
  let score = cell.centerWeight * 8 + cell.neighbors.length * 4;

  if (owner === player) score += level * 14;
  if (resultingLevel === MAX_LEVEL - 1) score += 52;
  if (resultingLevel >= MAX_LEVEL) score += 180 + blastPotential(state, cells, move, player) * 2.5;

  let adjacentEnemyPrimed = 0;
  for (const neighbor of cell.neighbors) {
    const neighborOwner = state.owners[neighbor];
    const neighborLevel = state.levels[neighbor];
    if (neighborOwner === opponent(player)) {
      score += resultingLevel >= MAX_LEVEL ? neighborLevel * 12 : neighborLevel * 1.5;
      if (neighborLevel === MAX_LEVEL - 1) adjacentEnemyPrimed += 1;
    } else if (neighborOwner === player) {
      score += Math.min(level, neighborLevel) * 1.5;
    }
  }

  if (resultingLevel < MAX_LEVEL && adjacentEnemyPrimed) {
    score -= adjacentEnemyPrimed * (owner === EMPTY ? 90 : 42 + resultingLevel * 8);
  }

  if (isEarlyPhase(state, cells)) {
    const profile = neighborhoodProfile(state, cells, move, player);
    if (owner === player) {
      score += resultingLevel * resultingLevel * 18;
      score += Math.max(0, resultingLevel - 2) * (70 + cell.centerWeight * 18);
      score += profile.friendlyAdjacent * 28;
      if (resultingLevel >= MAX_LEVEL - 2 && cell.neighbors.length === 6) score += 100;

      const tempo = resultingLevel - profile.enemyMaxLevel;
      score += tempo * 42;
      if (profile.enemyAdjacent > 0 && tempo <= 0) score -= (1 - tempo) * 72;
      if (profile.enemyAdjacent === 0 && level >= 2) score += 78;
    } else if (owner === EMPTY) {
      if (profile.friendlyAdjacent === 0) {
        score -= 125;
      } else {
        score += profile.friendlyAdjacent * 22 + profile.friendlyMaxLevel * 14;
      }
      if (profile.enemyAdjacent > 0 && profile.friendlyAdjacent === 0) score -= 180;
      if (profile.enemyAdjacent > profile.friendlyAdjacent) {
        score -= (profile.enemyAdjacent - profile.friendlyAdjacent) * 48;
      }
      if (cell.neighbors.length < 6 && profile.friendlyMaxLevel < 3 && profile.enemyAdjacent === 0) {
        score -= 55;
      }
    }
  }

  if (resultingLevel >= MAX_LEVEL) {
    const beforeTotals = totalsFor(state);
    const after = simulateMove(state, cells, move, player).state;
    const afterTotals = totalsFor(after);
    const ownBefore = playerTotals(beforeTotals, player);
    const enemyBefore = playerTotals(beforeTotals, opponent(player));
    const ownAfter = playerTotals(afterTotals, player);
    const enemyAfter = playerTotals(afterTotals, opponent(player));
    score += (ownAfter.score - ownBefore.score) * 12;
    score += (enemyBefore.score - enemyAfter.score) * 22;
    score += (ownAfter.tiles - ownBefore.tiles) * 18;
    score += (enemyBefore.tiles - enemyAfter.tiles) * 32;
    if (terminalWinner(after) === player) score += WIN_SCORE;
  }

  return score;
}

function evaluate(state, cells, player) {
  const winner = terminalWinner(state);
  if (winner === player) return WIN_SCORE - state.turnsPlayed;
  if (winner === opponent(player)) return -WIN_SCORE + state.turnsPlayed;
  if (winner === EMPTY) return 0;

  const totals = totalsFor(state);
  const own = playerTotals(totals, player);
  const enemy = playerTotals(totals, opponent(player));
  const earlyPhase = isEarlyPhase(state, cells);
  const tileWeight = earlyPhase ? 8 : 24;
  let score = (own.score - enemy.score) * 30 + (own.tiles - enemy.tiles) * tileWeight;

  for (let index = 0; index < cells.length; index += 1) {
    const owner = state.owners[index];
    if (owner === EMPTY) continue;
    const sign = owner === player ? 1 : -1;
    const level = state.levels[index];
    const cell = cells[index];
    let support = 0;
    let danger = 0;
    let friendlyAdjacent = 0;
    let enemyAdjacent = 0;
    let friendlyMaxLevel = 0;
    let enemyMaxLevel = 0;

    score += sign * (cell.centerWeight * 5 + cell.neighbors.length * 2);
    score += sign * level * Math.max(0, cell.neighbors.length - 3) * 2.2;
    if (cell.neighbors.length === 6) score += sign * level * 7;
    else if (cell.neighbors.length === 3) score -= sign * level * 10;
    else score -= sign * level * 4;

    for (const neighbor of cell.neighbors) {
      const neighborOwner = state.owners[neighbor];
      const neighborLevel = state.levels[neighbor];
      if (neighborOwner === owner) {
        friendlyAdjacent += 1;
        friendlyMaxLevel = Math.max(friendlyMaxLevel, neighborLevel);
        support += Math.min(level, neighborLevel) * 0.9;
      } else if (neighborOwner !== EMPTY) {
        enemyAdjacent += 1;
        enemyMaxLevel = Math.max(enemyMaxLevel, neighborLevel);
        if (neighborLevel === MAX_LEVEL - 1) {
          danger += level * 18 + (level === MAX_LEVEL - 1 ? 28 : 0);
        } else if (neighborLevel === MAX_LEVEL - 2) {
          danger += level * 4.5;
        }
      }
    }

    score += sign * support;
    score -= sign * danger;
    if (level === MAX_LEVEL - 1) {
      const tempo = state.currentPlayer === owner ? 1.18 : 0.72;
      score += sign * blastPotential(state, cells, index, owner) * tempo;
    } else if (level === MAX_LEVEL - 2) {
      score += sign * blastPotential(state, cells, index, owner) * 0.22;
    }

    if (earlyPhase) {
      let development = Math.max(0, level - 1) * (2 + friendlyAdjacent + cell.centerWeight);
      development += Math.min(level, 4) * Math.max(0, friendlyMaxLevel - 1);
      let racePenalty = 0;
      let buildup = Math.max(0, level - 2) * (8 + cell.centerWeight + friendlyAdjacent * 2);
      let scattered = 0;

      if (enemyAdjacent > 0) {
        const tempo = level - enemyMaxLevel;
        if (tempo < 0) racePenalty += Math.abs(tempo) * 6 + (friendlyAdjacent === 0 ? 12 : 0);
        else development += tempo * 4;
      }
      if (friendlyAdjacent === 0 && enemyAdjacent > 0 && level <= 2) {
        racePenalty += enemyAdjacent * 8 + level * 4;
      }
      if (level >= MAX_LEVEL - 2) {
        buildup += 10 + friendlyAdjacent * 3 + Math.max(0, level - enemyMaxLevel) * 4;
      }
      if (level === 1) {
        scattered += 10;
        if (friendlyAdjacent === 0) scattered += 22 + cell.centerWeight;
        if (enemyAdjacent > friendlyAdjacent) {
          scattered += 12 + (enemyAdjacent - friendlyAdjacent) * 6;
        }
        if (cell.neighbors.length < 6) scattered += 8;
      } else if (level === 2 && friendlyAdjacent === 0) {
        scattered += 8 + enemyAdjacent * 6;
      }

      score += sign * (
        development * 20
        + buildup * 22
        - racePenalty * 18
        - scattered * 16
      );
    }
  }

  return score;
}

function rankedMoves(state, cells, player, limit = Infinity, allowedMoves = null) {
  const moves = allowedMoves ?? legalMovesFor(state, player);
  return moves
    .map((move) => ({ move, score: movePriority(state, cells, move, player) }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(({ move }) => move);
}

function immediateWinningMoves(state, cells, player) {
  return legalMovesFor(state, player).filter((move) => {
    const after = simulateMove(state, cells, move, player).state;
    return terminalWinner(after) === player;
  });
}

function savingMoves(state, cells, player, legal) {
  const enemy = opponent(player);
  if (!immediateWinningMoves(state, cells, enemy).length) return null;
  return legal.filter((move) => {
    const after = simulateMove(state, cells, move, player).state;
    if (terminalWinner(after) === player) return true;
    return immediateWinningMoves(after, cells, enemy).length === 0;
  });
}

function chooseGreedy(state, cells, player, allowedMoves = null) {
  let bestScore = -Infinity;
  let choices = [];
  for (const move of rankedMoves(state, cells, player, Infinity, allowedMoves)) {
    const after = simulateMove(state, cells, move, player).state;
    const score = evaluate(after, cells, player);
    if (score > bestScore + 0.001) {
      bestScore = score;
      choices = [move];
    } else if (Math.abs(score - bestScore) <= 0.001) {
      choices.push(move);
    }
  }
  return choices[Math.floor(Math.random() * choices.length)];
}

function search(state, cells, rootPlayer, depth, alpha, beta, ply, extensions) {
  const winner = terminalWinner(state);
  if (winner !== null) return evaluate(state, cells, rootPlayer);

  const currentPlayer = state.currentPlayer;
  let forcingMoves = null;
  if (depth === 0 && extensions > 0) {
    forcingMoves = legalMovesFor(state, currentPlayer).filter(
      (move) => state.owners[move] === currentPlayer && state.levels[move] >= MAX_LEVEL - 1,
    );
  }
  if (depth === 0 && !forcingMoves?.length) return evaluate(state, cells, rootPlayer);

  const limit = forcingMoves ? forcingMoves.length : SEARCH_LIMITS[Math.min(ply, SEARCH_LIMITS.length - 1)];
  const moves = rankedMoves(state, cells, currentPlayer, limit, forcingMoves);
  if (!moves.length) return evaluate(state, cells, rootPlayer);

  const maximizing = currentPlayer === rootPlayer;
  let best = maximizing ? -Infinity : Infinity;
  for (const move of moves) {
    const after = simulateMove(state, cells, move, currentPlayer).state;
    const score = search(
      after,
      cells,
      rootPlayer,
      Math.max(0, depth - 1),
      alpha,
      beta,
      ply + 1,
      forcingMoves ? extensions - 1 : extensions,
    );
    if (maximizing) {
      best = Math.max(best, score);
      alpha = Math.max(alpha, best);
    } else {
      best = Math.min(best, score);
      beta = Math.min(beta, best);
    }
    if (beta <= alpha) break;
  }
  return best;
}

function chooseTactical(state, cells, player, allowedMoves = null) {
  const candidates = rankedMoves(state, cells, player, Infinity, allowedMoves);
  let bestScore = -Infinity;
  let choices = [];
  let alpha = -Infinity;

  for (const move of candidates) {
    const after = simulateMove(state, cells, move, player).state;
    const score = search(after, cells, player, 3, alpha, Infinity, 1, 1);
    if (score > bestScore + 0.001) {
      bestScore = score;
      choices = [move];
    } else if (Math.abs(score - bestScore) <= 0.001) {
      choices.push(move);
    }
    alpha = Math.max(alpha, bestScore);
  }

  return choices[Math.floor(Math.random() * choices.length)] ?? candidates[0];
}

export function chooseAiMove(game, difficulty = "tactical", player = game.state.currentPlayer) {
  const { state } = game;
  const cells = game.board.cells;
  const legal = legalMovesFor(state, player);
  if (!legal.length) return null;

  const wins = immediateWinningMoves(state, cells, player);
  if (wins.length) return wins[Math.floor(Math.random() * wins.length)];

  if (difficulty === "tactical" && state.turnsPlayed === 0) {
    return rankedMoves(state, cells, player, 1)[0];
  }

  let candidates = legal;
  if (difficulty !== "easy") {
    const saves = savingMoves(state, cells, player, legal);
    if (saves?.length) candidates = saves;
  }

  if (difficulty === "easy") {
    const weighted = rankedMoves(state, cells, player);
    const pool = weighted.slice(0, Math.max(4, Math.ceil(weighted.length * 0.7)));
    return pool[Math.floor(Math.random() * pool.length)];
  }
  if (difficulty === "standard") return chooseGreedy(state, cells, player, candidates);
  return chooseTactical(state, cells, player, candidates);
}

export const __testing = {
  evaluate,
  immediateWinningMoves,
  isEarlyPhase,
  movePriority,
  savingMoves,
  search,
};
